import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { Card, CardContent } from "@/components/ui/card";
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

import { Plus, Trash2 } from "lucide-react";

import type { AutorizacionSeccionRadioProps } from "./AutorizacionSeccionRadio";

import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { TIPOS_DOCUMENTO } from "@/config/form-options";
import { useEffect } from "react";

export function AutorizacionSeccionPep({
  name,
  title,
  description,
  readOnly,
}: AutorizacionSeccionRadioProps) {
  const { control } = useFormContext();

  // 👀 Observar la respuesta seleccionada
  const respuesta = useWatch({
    control,
    name: `${name}.respuesta`,
  });

  const {
    fields: miembros,
    append,
    remove,
  } = useFieldArray({
    control,
    name: `${name}.miembros`,
  });

  useEffect(() => {
    if (respuesta === "no" && miembros.length > 0) {
      remove(); // sin argumentos elimina todos los elementos del array
    }
  }, [respuesta, miembros.length, remove]);

  return (
    <>
      <Card>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">{title}</h2>
            <p className="text-sm text-muted-foreground whitespace-pre-line">
              {description}
            </p>
          </div>

          {/* 👇 Solo mostrar radios si NO es readOnly */}
          {!readOnly && (
            <FormField
              name={`${name}.respuesta`}
              control={control}
              render={({ field }) => {
                return (
                  <FormItem className="space-y-2">
                    <FormLabel>Seleccione una opción</FormLabel>
                    <FormControl>
                      <RadioGroup
                        value={field.value || ""}
                        onValueChange={field.onChange}
                        className="flex gap-4"
                      >
                        {[
                          { label: "Sí", value: "si" },
                          { label: "No", value: "no" },
                        ].map((op) => (
                          <div
                            key={op.value}
                            className="flex items-center space-x-2"
                          >
                            <RadioGroupItem
                              value={op.value}
                              id={`${name}-${op.value}`}
                            />
                            <FormLabel htmlFor={`${name}-${op.value}`}>
                              {op.label}
                            </FormLabel>
                          </div>
                        ))}
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          {/* Miembros PEP */}
          {respuesta === "si" && (
            <FormField
              name={`${name}.miembros`}
              control={control}
              render={() => (
                <FormItem>
                  <div className="space-y-4">
                    {miembros.map((field, index) => (
                      <Card key={field.id} className="p-4 shadow-sm border">
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3 p-4 border rounded-2xl shadow-sm relative">
                          <FormField
                            name={`${name}.miembros.${index}.nombreCompleto`}
                            control={control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Nombre completo</FormLabel>
                                <FormControl>
                                  <Input
                                    {...field}
                                    placeholder="Nombre completo"
                                  />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />

                          <FormField
                            control={control}
                            name={`${name}.miembros.${index}.tipoDocumento`}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Tipo de documento</FormLabel>
                                <FormControl>
                                  <Select
                                    onValueChange={field.onChange}
                                    value={field.value ?? ""}
                                  >
                                    <SelectTrigger className="w-full">
                                      <SelectValue placeholder="Selecciona el tipo de documento" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {TIPOS_DOCUMENTO.map((t) => (
                                        <SelectItem
                                          key={t.value}
                                          value={t.value}
                                        >
                                          {t.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={`${name}.miembros.${index}.numeroDocumento`}
                            control={control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Número de documento</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Número" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={`${name}.miembros.${index}.cargo`}
                            control={control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Cargo</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Cargo" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={`${name}.miembros.${index}.fechaInicio`}
                            control={control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Fecha inicio</FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={`${name}.miembros.${index}.fechaFin`}
                            control={control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Fecha finalización</FormLabel>
                                <FormControl>
                                  <Input {...field} type="date" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                          <FormField
                            name={`${name}.miembros.${index}.parentesco`}
                            control={control}
                            render={({ field }) => (
                              <FormItem>
                                <FormLabel>Parentesco</FormLabel>
                                <FormControl>
                                  <Input {...field} placeholder="Parentesco" />
                                </FormControl>
                                <FormMessage />
                              </FormItem>
                            )}
                          />
                        </div>
                        <div className="flex justify-end mt-2">
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            onClick={() => remove(index)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Eliminar
                          </Button>
                        </div>
                      </Card>
                    ))}
                    <FormMessage />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() =>
                        append({
                          nombreCompleto: "",
                          tipoDocumento: "",
                          numeroDocumento: "",
                          cargo: "",
                          fechaInicio: "",
                          fechaFin: "",
                          parentesco: "",
                        })
                      }
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Agregar miembro PEP
                    </Button>
                  </div>
                </FormItem>
              )}
            />
          )}
        </CardContent>
      </Card>
    </>
  );
}
